import React, { useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const MOBILE_BREAKPOINT = 768;

const BatchWiseStudentCount = ({ batchData, batchDetails, loading }) => {
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  console.log(batchData);
  console.log(batchDetails);

  // Show loading state
  if (loading) {
    return (
      <NoDataWrapper>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </NoDataWrapper>
    );
  }

  // Check if batchData is null, undefined, or empty array
  const isDataEmpty = !batchData || batchData.length === 0;

  // Show "No batches available" message when data is empty
  if (isDataEmpty) {
    return (
      <NoDataWrapper>
        <div className="no-data-message">
          <img src="/assets/images/nostudent-enrolled.png" alt="No Data" className="img-fluid" style={{ maxHeight: "100px" }} />
          <p>No batch data available</p>
        </div>
      </NoDataWrapper>
    );
  }

  return (
    <StyledWrapper>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        autoplay={{
          delay: 3500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        ref={swiperRef}
        loop={batchData.length > 1}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          [MOBILE_BREAKPOINT]: {
            slidesPerView: 2,
            spaceBetween: 25,
          },
          1200: {
            slidesPerView: 3,
            spaceBetween: 30,
          },
          1600: {
            slidesPerView: 4,
            spaceBetween: 30,
          },
        }}
        onInit={(swiper) => {
          swiperRef.current = swiper;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.navigation.init();
          swiper.navigation.update();
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        className="batch-swiper"
      >
        {batchData.map((batch, index) => (
          <SwiperSlide key={index}>
            <div className="card">
              {/* Background SVG */}
              <div className="BG">
                <i className="bi bi-people-fill icon"></i>
              </div>
              
              {/* Batch Content */}
              <div className="content">
                <p className="heading">
                  {batch.name || 'Unknown Batch'}
                </p>
                
                <div className="student-stats">
                  <p className="student-count">{batch.value}</p>
                  <p className="student-label">
                    {batch.value === 1 ? 'Student' : 'Students'}
                  </p>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledWrapper>
  );
};

const NoDataWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  width: 100%;

  .no-data-message {
    text-align: center;
    color: #666;
    
    p {
      margin-top: 1rem;
      font-size: 1.1rem;
    }
  }

  .spinner-border {
    width: 3rem;
    height: 3rem;
  }
`;

const StyledWrapper = styled.div`
  .batch-swiper {
    padding: 20px 0 20px ;
  }

  .batch-swiper .swiper-slide {
    height: auto;
    display: flex;
  }

  .card {
    width: 100%;
    height: 220px;
    position: relative;
    background: linear-gradient(135deg, rgba(108, 99, 255, 0.1) 0%, rgba(227, 231, 253, 0.9) 100%);
    border-bottom: 3px solid #6c63ff;
    overflow: hidden;
    box-shadow: 0px 12px 40px -20px rgba(108, 99, 255, 0.4);
    border-radius: 12px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .BG {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .BG .icon {
    position: absolute;
    font-size: 8rem;
    left: -15%;
    top: -25%;
    opacity: 0.15;
    color: #6c63ff;
    transition: all 0.5s ease;
  }

  .content {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: rgb(30, 30, 30);
    gap: 8px;
  }

  .heading {
    font-size: 1.25em;
    font-weight: 700;
    color: #2d3748;
    text-align: center;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 2.6em;
    margin: 0;
  }

  .program-title {
    font-size: 0.85em;
    color: #718096;
    text-align: center;
    margin: 0;
    font-weight: 500;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .student-stats {
    text-align: center;
    margin-top: 8px;
  }

  .student-count {
    font-size: 2.5rem;
    font-weight: bold;
    color: #6c63ff;
    margin: 0;
    line-height: 1;
  }

  .student-label {
    font-size: 0.95em;
    color: rgb(100, 100, 100);
    margin: 4px 0 0 0;
    font-weight: 500;
  }

  .card:hover .BG .icon {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(15deg) scale(1.8);
    opacity: 0.08;
  }

  .card:hover {
    transform: translateY(-8px);
    box-shadow: 0px 20px 60px -15px rgba(108, 99, 255, 0.5);
    border-bottom-color: #5a52d5;
  }

  .card:hover .student-count {
    color: #5a52d5;
  }

  /* Swiper Navigation Styles */
  .batch-swiper .swiper-button-next,
  .batch-swiper .swiper-button-prev {
    color: #6c63ff;
    background: white;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(108, 99, 255, 0.25);
    transition: all 0.3s ease;
    top: 40%;
  }

  .batch-swiper .swiper-button-next:after,
  .batch-swiper .swiper-button-prev:after {
    font-size: 18px;
    font-weight: bold;
  }

  .batch-swiper .swiper-button-next:hover,
  .batch-swiper .swiper-button-prev:hover {
    background: #6c63ff;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(108, 99, 255, 0.4);
  }

  .batch-swiper .swiper-button-disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  /* Swiper Pagination Styles */
  .batch-swiper .swiper-pagination {
    bottom: 10px;
  }

  .batch-swiper .swiper-pagination-bullet {
    background: #cbd5e0;
    opacity: 1;
    width: 12px;
    height: 12px;
    margin: 0 6px;
    transition: all 0.3s ease;
  }

  .batch-swiper .swiper-pagination-bullet-active {
    background: #6c63ff;
    transform: scale(1.3);
  }

  .batch-swiper .swiper-pagination-bullet:hover {
    background: #8b83ff;
    transform: scale(1.1);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .card {
      height: 240px;
    }
    
    .student-count {
      font-size: 2rem;
    }
    
    .heading {
      font-size: 1.1rem;
    }

    .program-title {
      font-size: 0.8em;
    }

    .batch-swiper {
      padding: 15px 0 40px 0;
    }

    .batch-swiper .swiper-button-next,
    .batch-swiper .swiper-button-prev {
      width: 36px;
      height: 36px;
    }

    .batch-swiper .swiper-button-next:after,
    .batch-swiper .swiper-button-prev:after {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .batch-swiper .swiper-button-next,
    .batch-swiper .swiper-button-prev {
      display: none;
    }

    .card {
      height: 220px;
    }
  }
`;

export default BatchWiseStudentCount;